import { useSnackbar } from "notistack"
import { crudGetList, RecordMap, ReduxState, useCreateController, useGetList } from "ra-core"
import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { RecPatch } from "@/dataProvider/tools"
import { MutationCallbacks } from "@/dataProvider/types"
import { CreateFieldTagInput, FieldTagResponseDto, ReasonCollectResponseDto, RetentionRuleResponseDto, SecondaryClassificationResponseDto } from "@/gen/api/governance"
import chainOptions from "@/utils/chainOptions"
import { pages, sorts } from "@/utils/paramsWithGetMainList"
import retrieveAxiosErr from "@/utils/retrieveAxiosErr"
import { useDataCatalogContext } from "../dataCatalogContext/DataCatalogContext"


export interface PathInfo {database?: number, schema?: string, table?: string}

interface AmbientFacilitiesControllerProps {
  loading: boolean,
  databaseOptions: SimpleOption[],
  schemaOptions: SimpleOption2[],
  tableOptions: SimpleOption[],
  fieldOptions: SimpleOption[],
  classifications: SimpleOption[],
  tagOptions: SimpleOption[],
  pathInfo: PathInfo,
  setPathInfo: (x:PathInfo) => void,
  retentionRules: RetentionRuleResponseDto[],
  reasonCollects: ReasonCollectResponseDto[],
  tagCreate: (sTag: string, callbacks?: MutationCallbacks) => void,
}

export const AmbientFacilitiesContext = createContext<AmbientFacilitiesControllerProps>({
  loading: false,
  databaseOptions: [],
  schemaOptions: [],
  tableOptions: [],
  fieldOptions: [],
  classifications: [],
  tagOptions: [],
  pathInfo: {},
  setPathInfo: () => {},
  retentionRules: [],
  reasonCollects: [],
  tagCreate: () => {},
})


export const AmbientFacilitiesContextProvider: FC = ({ children }) => {
  const value = useAmbientFacilitiesController()
  return (
    <AmbientFacilitiesContext.Provider value={value}>
      {children}
    </AmbientFacilitiesContext.Provider>
  )
}

export interface SimpleOption {
  key: number,
  text: string,
}
export interface SimpleOption2 {
  key: string,
  text: string,
}


export const useAmbientFacilitiesController = (): AmbientFacilitiesControllerProps => {
  const [pathInfo, setPathInfo] = useState<PathInfo>({})
  
  const { databaseNameData } = useDataCatalogContext()
  const { data: schemaNameData, loading: loading1 }   = useGetList<SimpleOption2&RecPatch>(pathInfo?.database ? 'govrnc-schemas' : 'noop', pages(), sorts(), { database: pathInfo.database }, {action:'DATA_CATA_CTX'})
  const { data: tableNameData, loading: loading2 }    = useGetList<SimpleOption&RecPatch>(pathInfo?.database && pathInfo.schema ? 'govrnc-tables' : 'noop', pages(), sorts(), { database: pathInfo.database, schema: pathInfo.schema }, {action:'DATA_CATA_CTX'})
  const { data: fieldNameData, loading: loading3 }    = useGetList<SimpleOption&RecPatch>(pathInfo?.database && pathInfo.schema && pathInfo.table ? 'govrnc-fields' : 'noop', pages(), sorts(), { database: pathInfo.database, schema: pathInfo.schema, table: pathInfo.table }, {action:'DATA_CATA_CTX'})
  
  const skipOption = { key: undefined as any, text: '*' }
  const databaseOptions = useMemo(() => [skipOption, ...getValues(databaseNameData, (a, b) => (a.text || '') < (b.text || '') ? -1 : 1)], [databaseNameData]) as SimpleOption[]
  const schemaOptions   = useMemo(() => [skipOption, ...getValues(schemaNameData)], [schemaNameData])
  const tableOptions    = useMemo(() => [skipOption, ...getValues(tableNameData )], [tableNameData])
  const fieldOptions    = useMemo(() => [skipOption, ...getValues(fieldNameData )], [fieldNameData])
  
  const classificationData = useSelector<ReduxState, RecordMap<SecondaryClassificationResponseDto&RecPatch>>(store => store.admin.resources['settings/governance/data-classification']?.data || {})
  const classifications: SimpleOption[] = 
    useMemo(() => 
      Object.values<SecondaryClassificationResponseDto>(classificationData)
        .map(c => ({
          ...c, 
          key: c.secondaryClassificationId || -1, 
          text: `${c.primaryClassificationText} - ${c.name}` || ''
        })).sort((a, b) => 
                ((a.primaryClassification || 0) - (b.primaryClassification || 0)) 
             || ((a.secondaryClassificationId || 0) - (b.secondaryClassificationId || 0))), 
      [classificationData]
    )
    
  const tagData = useSelector<ReduxState, Record<string, FieldTagResponseDto&RecPatch>>(store => store.admin.resources['govrnc-tags']?.data || {})
  const tagOptions: SimpleOption[] = useMemo(() => Object.values(tagData).map(t => ({...t, key: t.tagId || 0, text: t.name || '' })).sort(), [tagData])
  
  const retentionRulesData = useSelector<ReduxState, Record<string, RetentionRuleResponseDto&RecPatch>>(store => store.admin.resources['govrnc-retention-rule']?.data || {})
  const retentionRules = Object.values(retentionRulesData).sort((a, b) => (a.name || '') < (b.name || '') ? -1 : 1)
  
  const reasonCollectsData = useSelector<ReduxState, Record<string, ReasonCollectResponseDto&RecPatch>>(store => store.admin.resources['govrnc-reason-collect']?.data || {})
  const reasonCollects = Object.values(reasonCollectsData).sort((a, b) => (a.reasonText || '') < (b.reasonText || '') ? -1 : 1)
  
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(crudGetList('settings/governance/data-classification', pages(), sorts(), { useCache: true }))
    dispatch(crudGetList('govrnc-tags', pages(), sorts(), { useCache: true }))
    dispatch(crudGetList('govrnc-retention-rule', pages(), sorts(), {}))
    dispatch(crudGetList('govrnc-reason-collect', pages(), sorts(), {}))
  }, [])

  const loading = loading1 || loading2 || loading3

  const { enqueueSnackbar } = useSnackbar()

  const { save: tagCreate } = useCreateController<CreateFieldTagInput&RecPatch>({ resource: 'govrnc-tags', basePath: '' })
  const tagCreateWrapped = useCallback((sTag: string, callbacks?: MutationCallbacks) => {
    tagCreate({ name: sTag }, '', chainOptions(callbacks, {
      onSuccess: () => {},
      onFailure: (err:any) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Failed to create tag: ${errMsg}`, { variant: 'error' })
      },
    }))
  }, [tagCreate, enqueueSnackbar])

  return {
    loading,
    databaseOptions,
    schemaOptions,
    tableOptions,
    fieldOptions,
    classifications,
    tagOptions,
    pathInfo,
    setPathInfo,
    retentionRules,
    reasonCollects,
    tagCreate: tagCreateWrapped,
  }
}

export const useAmbientFacilitiesContext = () => useContext(AmbientFacilitiesContext);

function getValues<T>(bag?: Record<string, T>, sorter?: (a:T, b:T) => number) : T[] {
  return Object.values(bag || {}).sort(sorter)
}

